import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { Routes } from './routes.class';
import { ApiResponse } from 'src/app/models/interfaces/api-response.interface';
import { map } from 'rxjs/operators';
import { PortfolioSeriesDashboard } from 'src/app/models/classes/portfolio-series-dashboard.class';
import { DealDashboard } from 'src/app/models/classes/deal-dashboard.class';
import { AssetDashboard } from 'src/app/models/classes/asset-dashboard.class';
import { PortfolioSeriesMeta } from 'src/app/models/classes/portfolio-series-meta.class';
import { DealMeta } from 'src/app/models/classes/deal-meta.class';
import { InvestorDashboard } from 'src/app/models/classes/investor-dashboard.class';
import { AssetMeta } from 'src/app/models/classes/asset-meta.class';
import { SupportRequest } from 'src/app/models/classes/support-request.class';
import { InvestorMeta } from 'src/app/models/classes/investor-meta.class';
import { UserActivity } from 'src/app/models/classes/user-activity.class';
import { PagedResponse } from 'src/app/models/classes/paged-response.class';
import { DocumentQuery } from 'src/app/models/classes/document-query.class';
import { PortfolioDashboard } from 'src/app/models/classes/portfolio-dashboard.class';
import { PortfolioMetadata } from 'src/app/models/classes/portfolio.class';

const hostname = location.origin.includes('localhost:4200') ? 'https://localhost:5001/' : '';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private httpClient = inject(HttpClient);

  getInvestors(): Observable<InvestorMeta[]> {
    const route = `${hostname + Routes.metadata}/investors`;
    return this.httpClient
      .get<ApiResponse<InvestorMeta[]>>(route)
      .pipe(map(response => {
        return response.data
      }));
  }

  getInvestorPortfolioDashboardDetails(investorId: number, reportingPeriod: string): Observable<InvestorDashboard> {
    let route = `${hostname + Routes.dashboardInvestor}/${investorId}`;
    if (reportingPeriod) route += `?reportingPeriod=${reportingPeriod}`;
    return this.httpClient
      .get<ApiResponse<InvestorDashboard>>(route)
      .pipe(map(response => {
        return response.data;
      }));
  }

  getPortfolioDashboardDetails(portfolioId: number, reportingPeriod: string): Observable<PortfolioDashboard> {
    let route = `${hostname + Routes.dashboardPortfolio}/${portfolioId}`;
    if (reportingPeriod) route += `?reportingPeriod=${reportingPeriod}`;
    return this.httpClient
      .get<ApiResponse<PortfolioDashboard>>(route)
      .pipe(map(response => {
        return response.data;
      }));
  }

  getPortfolioSeriesDashboardDetails(investorId: number, seriesId: number, reportingPeriod: string): Observable<PortfolioSeriesDashboard> {
    let route = `${hostname + Routes.dashboardInvestor}/${investorId}/portfolioseries/${seriesId}`;
    if (reportingPeriod) route += `?reportingPeriod=${reportingPeriod}`;
    return this.httpClient
      .get<ApiResponse<PortfolioSeriesDashboard>>(route)
      .pipe(map(response => {
        return response.data;
      }));
  }

  getDealDashboardDetails(investorId: number, seriesId: number, dealId: number, reportingPeriod: string): Observable<DealDashboard> {
    let route = `${hostname + Routes.dashboardInvestor}/${investorId}/portfolioseries/${seriesId}/deals/${dealId}`;
    if (reportingPeriod) route += `?reportingPeriod=${reportingPeriod}`;
    return this.httpClient
      .get<ApiResponse<DealDashboard>>(route)
      .pipe(map(response => {
        return response.data;
      }));
  }

  async getAssetDashboardDetails(investorId: number, seriesId: number, positionId: number, assetId: number, reportingPeriod: string): Promise<AssetDashboard> {
    const route = `${hostname + Routes.dashboardInvestor}/${investorId}/portfolioseries/${seriesId}/deals/${positionId}/assets/${assetId}?reportingPeriod=${reportingPeriod}`;
    return await lastValueFrom(this.httpClient
      .get<ApiResponse<AssetDashboard>>(route)
      .pipe(map(response => {
        return response.data;
      })));
  }

  getSnapshotRoute(reportingPeriod: string, investorId: number, seriesId: number, dealId?: number, assetMasterId?: number, caseStudy?: boolean): string {
    let route = `${hostname + Routes.dashboardInvestor}/${investorId}/portfolioseries/${seriesId}`;

    if (dealId) {
      route += `/deals/${dealId}`;
    }

    if (assetMasterId) {
      route += `/assets/${assetMasterId}`;
    }

    if (caseStudy) {
      route += `/casestudy?fiscalDate=${reportingPeriod}`;
    } else {
      route += `/report?reportingPeriod=${reportingPeriod}`;
    }
    return route;
  }

  downloadSnapshot(url: string): Observable<HttpResponse<Blob>> {
    return this.httpClient
      .get(url, { observe: 'response', responseType: 'blob' });
  }

  getPortfolioSeriesDashboardMetadata(seriesId: number, investorId: number): Observable<PortfolioSeriesMeta> {
    const route = `${hostname + Routes.metadata}/investors/${investorId}/portfolioSeries/${seriesId}`;
    return this.httpClient
      .get<ApiResponse<PortfolioSeriesMeta>>(route)
      .pipe(map(response => {
        return response.data;
      }));
  }

  getDealDashboardMetadata(seriesId: number, dealId: number): Observable<DealMeta> {
    const route = `${hostname + Routes.metadata}/portfolioSeries/${seriesId}/deals/${dealId}`;
    return this.httpClient
      .get<ApiResponse<DealMeta>>(route)
      .pipe(map(response => {
        return response.data;
      }));
  }

  getAssetDashboardMetadata(seriesId: number, positionId: number, assetMasterId: number): Observable<AssetMeta> {
    const route = `${hostname + Routes.metadata}/portfolioSeries/${seriesId}/deals/${positionId}/assets/${assetMasterId}`;
    return this.httpClient
      .get<ApiResponse<AssetMeta>>(route)
      .pipe(map(response => {
        return response.data;
      }));
  }

  contactSupport(supportRequest: SupportRequest): Observable<ApiResponse<unknown>> {
    const route = `${hostname + Routes.contactSupport}`;

    return this.httpClient
      .post<ApiResponse<unknown>>(route, supportRequest)
      .pipe(map(response => {
        return response;
      })
      );
  }

  getPortfoliosMetadata(): Observable<PortfolioMetadata[]> {
    const route = `${hostname + Routes.metadata}/portfolios`;
    return this.httpClient
      .get<ApiResponse<[]>>(route)
      .pipe(map(response => {
        return response.data
      }));
  }

  updateUserActivity(userActivity: UserActivity): Observable<unknown> {
    const route = hostname + Routes.userActivityLog;
    return this.httpClient
      .post<ApiResponse<unknown>>(route, userActivity)
      .pipe(map(response => {
        return response;
      }));
  }

  getDocumentReports(documentQuery: DocumentQuery): Observable<PagedResponse<unknown>> {
    let url = `${hostname + Routes.reportStatus}/statuses?page=${documentQuery.page}&pageSize=${documentQuery.pageSize}`;
    if (documentQuery.sort && documentQuery.sortDesc != null) { url += `&sort=${documentQuery.sort}&sortDesc=${documentQuery.sortDesc}`; }
    if (documentQuery.status?.length > 0) { url += `&status=${documentQuery.status}`; }
    if (documentQuery.documentDates?.length > 0) { url += `&documentDates=${documentQuery.documentDates}`; }
    if (documentQuery.reportGenerationStartDate || documentQuery.reportGenerationEndDate) { url += `&reportGenerationStartDate=${documentQuery.reportGenerationStartDate}&reportGenerationEndDate=${documentQuery.reportGenerationEndDate}`; }
    if (documentQuery.portfolioIds?.length > 0) { url += `&portfolioIds=${documentQuery.portfolioIds}`; }
    if (documentQuery.investorIds?.length > 0) { url += `&investorIds=${documentQuery.investorIds}`; }

    return this.httpClient
      .get<ApiResponse<PagedResponse<unknown>>>(url)
      .pipe(map(response => response?.data));
  }

  retryDocumentGeneration(id: number): Observable<string> {
    const route = `${hostname + Routes.reportStatus}/${id}/retry`;
    return this.httpClient
      .post<ApiResponse<string>>(route, id)
      .pipe(map(response => {
        return response.data;
      }));
  }
}